import React, { useState } from "react";
import { apiPost } from "@/lib/api";
import { Bell, CheckCircle2 } from "lucide-react";
import ConsentMicrocopy from "@/components/ConsentMicrocopy";

/**
 * Medicare "Coming Soon" waitlist mini-form. Persists the interest via
 * /api/leads/partial with source="medicare-waitlist" so the team can reach
 * out when Medicare offerings launch.
 */
export const MedicareWaitlist = ({ context = {}, label, description }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErr("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/leads/partial", {
        email,
        source: "medicare-waitlist",
        source_context: { ...context },
      });
      setDone(true);
    } catch {
      setErr("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="medicare-waitlist"
      className="rounded-2xl border border-lw-gold/40 bg-lw-ivory-warm p-6 sm:p-8"
    >
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-lw-gold" />
        <span className="lw-eyebrow" style={{ marginBottom: 0 }}>
          {label || "Medicare — coming soon"}
        </span>
      </div>
      <h3 className="mt-3 font-serif-display text-xl sm:text-2xl text-lw-navy leading-snug">
        {description || "Medicare product offerings are being onboarded."}
      </h3>
      <p className="mt-2 text-sm text-lw-muted leading-relaxed">
        Content on this page is educational only and does not represent a
        specific plan offer. Join the waitlist and we&apos;ll be in touch when
        our Medicare offerings launch.
      </p>
      {done ? (
        <p
          data-testid="medicare-waitlist-success"
          className="mt-5 inline-flex items-center gap-2 text-sm text-lw-navy font-semibold"
        >
          <CheckCircle2 size={18} className="text-lw-gold" /> You&apos;re on the
          list. We&apos;ll reach out at launch.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="medicare-waitlist-email"
            className="flex-1 rounded-full border border-lw-navy/15 bg-white px-4 py-2.5 text-sm font-sans-ui outline-none focus:border-lw-gold"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="medicare-waitlist-submit"
            className="btn-gold sm:whitespace-nowrap disabled:opacity-60"
          >
            {busy ? "Adding…" : "Join the waitlist"}
          </button>
        </form>
      )}
      {err && (
        <p className="mt-3 text-sm text-red-600" data-testid="medicare-waitlist-error">
          {err}
        </p>
      )}
      <div className="mt-4">
        <ConsentMicrocopy />
      </div>
      <p
        data-testid="medicare-tpmo"
        className="mt-4 pt-4 border-t border-lw-navy/10 text-[11px] text-lw-muted leading-relaxed"
      >
        TPMO Notice (applicable upon Medicare product launch): We do not offer
        every plan available in your area. We will represent [N] organizations
        that offer [M] products in your area. Please contact Medicare.gov,
        1-800-MEDICARE (TTY users 1-877-486-2048) 24 hours a day / 7 days a
        week, or your local State Health Insurance Assistance Program (SHIP)
        to get information on all of your options. Not affiliated with or
        endorsed by Medicare, CMS, or the U.S. government.
      </p>
    </div>
  );
};

export default MedicareWaitlist;
